import React, { useEffect } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../utils/iconFallbacks';
import type { ImportFileEntry } from '../types';

interface ValidationStepProps {
  files: ImportFileEntry[];
  onRunValidation: () => void;
}

const ValidationStep: React.FC<ValidationStepProps> = ({ files, onRunValidation }) => {
  const { t } = useTranslation('mapDialogs');

  useEffect(() => {
    onRunValidation();
  }, []);

  const tabularFiles = files.filter(f => f.mappingStatus !== 'not-required' && !f.parseError);
  const gtfsFiles = files.filter(f => f.mappingStatus === 'not-required');
  const allValid = tabularFiles.every(f => f.validationResult?.isValid);

  return (
    <Stack spacing={2}>
      {allValid && tabularFiles.length > 0 && (
        <Alert severity="success">
          {t('importWizard.validationStep.allValid')}
        </Alert>
      )}

      {tabularFiles.map(file => {
        const result = file.validationResult;
        if (!result) return null;

        const errorCount = result.errors.length;
        const warningCount = result.warnings.length;

        return (
          <Accordion key={file.id} defaultExpanded={!result.isValid}>
            <AccordionSummary expandIcon={<Icon d={ICON_FALLBACKS.ExpandMoreOutlined} size={20} />}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%' }}>
                <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }}>
                  {file.fileName}
                </Typography>
                <Chip
                  size="small"
                  label={`${result.validRowCount}/${result.totalRowCount} rows valid`}
                  color={result.isValid ? 'success' : 'warning'}
                  sx={{ height: 22 }}
                />
                {errorCount > 0 && (
                  <Chip
                    size="small"
                    label={`${errorCount} error${errorCount !== 1 ? 's' : ''}`}
                    color="error"
                    sx={{ height: 22 }}
                  />
                )}
                {warningCount > 0 && (
                  <Chip
                    size="small"
                    label={`${warningCount} warning${warningCount !== 1 ? 's' : ''}`}
                    color="warning"
                    sx={{ height: 22 }}
                  />
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              {result.validRowCount > 0 && result.validRowCount < result.totalRowCount && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {t('importWizard.validationStep.rowsSkipped', { count: result.totalRowCount - result.validRowCount })}
                </Alert>
              )}
              {errorCount === 0 && warningCount === 0 && (
                <Typography variant="body2" color="text.secondary">
                  {t('importWizard.validationStep.noIssues')}
                </Typography>
              )}

              {result.errors.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="error.main" sx={{ mb: 1 }}>
                    {t('importWizard.validationStep.errors')}
                  </Typography>
                  <Stack spacing={0.5}>
                    {result.errors.slice(0, 50).map((err, idx) => (
                      <Typography key={idx} variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {err.row > 0 ? `Row ${err.row}` : 'Mapping'} · {err.column}: {err.message}
                      </Typography>
                    ))}
                    {result.errors.length > 50 && (
                      <Typography variant="caption" color="text.secondary">
                        ... and {result.errors.length - 50} more errors
                      </Typography>
                    )}
                  </Stack>
                </Box>
              )}

              {result.warnings.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="warning.main" sx={{ mb: 1 }}>
                    {t('importWizard.validationStep.warnings')}
                  </Typography>
                  <Stack spacing={0.5}>
                    {result.warnings.slice(0, 20).map((warn, idx) => (
                      <Typography key={idx} variant="caption" sx={{ fontFamily: 'monospace' }}>
                        Row {warn.row} · {warn.column}: {warn.message}
                      </Typography>
                    ))}
                    {result.warnings.length > 20 && (
                      <Typography variant="caption" color="text.secondary">
                        ... and {result.warnings.length - 20} more warnings
                      </Typography>
                    )}
                  </Stack>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}

      {gtfsFiles.length > 0 && (
        <Alert severity="info">
          {t('importWizard.validationStep.gtfsAutoValidated', { count: gtfsFiles.length })}
        </Alert>
      )}
    </Stack>
  );
};

export default ValidationStep;
